'use client'

import React, { useState, useRef, useEffect } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
// Import our API utilities
import { fetchSomosAi, handleApiError, getApiBaseUrl, ENDPOINTS, testBackendConnection } from '../utils/api';

// Define message types with proper type constraints
type MessageType = 'REJECT' | 'DETAILED' | 'CONVERSATION' | 'ERROR' | 'THINKING' | 'LOADING' | 'GREETING' | 'GOODBYE' | 'THANKS' | 'PARTIAL' | 'ACTION';
type MessageRole = 'user' | 'assistant';

interface Message {
  role: MessageRole;
  content: string;
  type?: MessageType;
}

// Add this interface near the top with other types
interface ApiResponse {
  content?: string;
  message?: string;
  type?: MessageType;
  role?: MessageRole;
}

// Define category value statements that will replace the complex questions
const categoryValueStatements = {
  education: {
    english: "Somos Tejas fights for fully funded public schools, fair teacher pay, quality early education, and educational equity for all students. How can we help you today?",
    spanish: "Somos Tejas lucha por escuelas públicas completamente financiadas, salarios justos para maestros, educación temprana de calidad y equidad educativa para todos los estudiantes. ¿Cómo podemos ayudarte hoy?"
  },
  immigration: {
    english: "Somos Tejas advocates for a fair and just immigration system that protects the rights and dignity of all individuals, regardless of their status, and strongly opposes unlawful detentions, deportations, and racial profiling of immigrant communities. How can we help you today?",
    spanish: "Somos Tejas aboga por un sistema de inmigración justo que proteja los derechos y la dignidad de todas las personas, independientemente de su estatus, y se opone firmemente a las detenciones ilegales, deportaciones y perfilamiento racial de las comunidades inmigrantes. ¿Cómo podemos ayudarte hoy?"
  },
  healthcare: {
    english: "Somos Tejas advocates for increased access to comprehensive, high-quality health care services for all. How can we help you today?",
    spanish: "Somos Tejas aboga por un mayor acceso a servicios de atención médica integrales y de alta calidad para todos. ¿Cómo podemos ayudarte hoy?"
  },
  voting: {
    english: "Somos Tejas is dedicated to advocating for bold election reforms and ensuring that everyone in our community has the opportunity and right to civic participation. How can we help you today?",
    spanish: "Somos Tejas se dedica a abogar por reformas electorales audaces y garantizar que todos en nuestra comunidad tengan la oportunidad y el derecho a la participación cívica. ¿Cómo podemos ayudarte hoy?"
  },
  housing: {
    english: "At Somos Tejas, we believe access to adequate housing and shelter is a human right. How can we help you today?",
    spanish: "En Somos Tejas, creemos que el acceso a una vivienda y refugio adecuados es un derecho humano. ¿Cómo podemos ayudarte hoy?"
  },
  rights: {
    english: "At Somos Tejas, we believe everyone has the right to understand and access their legal rights, regardless of background, status, or zip code. How can we help you today?",
    spanish: "En Somos Tejas, creemos que todos tienen el derecho de entender y acceder a sus derechos legales, independientemente de su origen, estatus o código postal. ¿Cómo podemos ayudarte hoy?"
  }
};

// Language detection function
const detectLanguage = (text: string): 'english' | 'spanish' => {
  const spanishWords = ['soy', 'estoy', 'tengo', 'quiero', 'necesito', 'ayuda', 'por favor', 'gracias', 'hola', 'adiós', 'sí', 'no', 'cómo', 'qué', 'dónde', 'cuándo', 'por qué', 'familia', 'trabajo', 'casa', 'dinero', 'salud', 'educación', 'inmigración', 'ciudadanía', 'derechos', 'votación', 'vivienda', 'atención médica'];
  const words = text.toLowerCase().split(/\s+/);
  const spanishWordCount = words.filter(word => spanishWords.includes(word)).length;
  const spanishRatio = spanishWordCount / words.length;
  
  return spanishRatio > 0.3 ? 'spanish' : 'english';
};

// Category buttons for quick access to common topics
const CATEGORY_BUTTONS = [
  { 
    key: 'immigration', 
    en: 'Immigration Help', 
    es: 'Ayuda de Inmigración',
    icon: '🏛️'
  },
  { 
    key: 'healthcare', 
    en: 'Healthcare', 
    es: 'Atención Médica',
    icon: '🏥'
  },
  { 
    key: 'voting', 
    en: 'Voting Rights', 
    es: 'Derechos de Voto',
    icon: '🗳️'
  },
  { 
    key: 'housing', 
    en: 'Housing', 
    es: 'Vivienda',
    icon: '🏠'
  },
  { 
    key: 'education', 
    en: 'Education', 
    es: 'Educación',
    icon: '📚'
  },
  { 
    key: 'rights', 
    en: 'Legal Rights', 
    es: 'Derechos Legales',
    icon: '⚖️'
  }
];

export default function SomosTejasChatbot({ isWidget = false }) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [language, setLanguage] = useState<'english' | 'spanish'>('english');
  const [showCategories, setShowCategories] = useState(true);
  const [backendConnected, setBackendConnected] = useState(true);
  const [isInputFocused, setIsInputFocused] = useState(false);
  
  // Refs for DOM manipulation
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Check if backend is available (simplified for better UX)
  const checkServerRequirements = (): { backendConnected: boolean } => {
    return { backendConnected: true }; // Assume connected for better UX
  };

  // Check backend connection
  const checkBackendConnection = async () => {
    try {
      // Use absolute URL to ensure widget works on any website
      const healthUrl = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' 
        ? '/api/health'  // Local development
        : 'https://somos-tejas-v2.fly.dev/api/health';  // Production API
      
      const response = await fetch(healthUrl, { 
        method: 'GET',
        headers: { 'Accept': 'application/json' }
      });
      setBackendConnected(response.ok);
    } catch {
      setBackendConnected(false);
    }
  };

  // Process user questions
  const processQuestion = async (question: string) => {
    if (!question.trim()) return;

    const detectedLang = detectLanguage(question);
    setLanguage(detectedLang);
    setShowCategories(false);

    // Add user message
    const userMessage: Message = { role: 'user', content: question };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      // Use absolute URL to ensure widget works on any website
      const apiUrl = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' 
        ? '/api/somos-ai'  // Local development
        : 'https://somos-tejas-v2.fly.dev/api/somos-ai';  // Production API
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: ApiResponse = await response.json();
      const assistantMessage: Message = {
        role: 'assistant',
        content: data.content || data.message || 'Lo siento, no pude procesar tu pregunta.',
        type: data.type
      };

      setMessages(prev => [...prev, assistantMessage]);
      
      // If the response type is ERROR, show category buttons again
      if (data.type === 'ERROR') {
        setShowCategories(true);
      }
    } catch (error) {
      console.error('Error:', error);
      const errorMessage: Message = {
        role: 'assistant',
        content: detectedLang === 'spanish' 
          ? 'Lo siento, hubo un problema. Por favor intenta de nuevo.'
          : 'Sorry, there was a problem. Please try again.',
        type: 'ERROR'
      };
      setMessages(prev => [...prev, errorMessage]);
      // Show category buttons again after error so user can try different topics
      setShowCategories(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (inputValue.trim() && !isLoading) {
      processQuestion(inputValue.trim());
    }
  };

  // Handle category button clicks
  const handleCategoryClick = (category: any) => {
    // Instead of using complex questions, send the value statement as a user message
    // and immediately respond with the statement
    const statement = categoryValueStatements[category.key as keyof typeof categoryValueStatements];
    if (statement) {
      const selectedStatement = language === 'spanish' ? statement.spanish : statement.english;
      
      // EXTREMELY AGGRESSIVE formatting cleanup to remove ALL indentation issues
      let cleanedStatement = selectedStatement;
      
      // First split by newlines and process each line
      const lines = cleanedStatement.split('\n');
      const processedLines = [];
      
      for (let line of lines) {
        // Remove ALL leading whitespace - no exceptions
        line = line.replace(/^\s+/, '');
        
        // Skip completely empty lines during processing
        if (line.trim().length > 0) {
          processedLines.push(line.trim());
        }
      }
      
      // Join back with single newlines
      cleanedStatement = processedLines.join('\n');
      
      // Final cleanup passes
      cleanedStatement = cleanedStatement
        .replace(/\n{2,}/g, '\n') // Remove all multiple newlines
        .replace(/^\s+|\s+$/g, '') // Remove leading/trailing whitespace
        .replace(/^[\s\t]+/gm, '') // Remove any remaining line-level indentation
        .trim();
      
      // Final verification - ensure no line starts with any whitespace
      cleanedStatement = cleanedStatement
        .split('\n')
        .map(line => line.replace(/^[\s\t]*/, ''))
        .join('\n')
        .trim();
      
      // Hide categories and show the conversation
      setShowCategories(false);
      
      // Add an assistant message with the value statement
      const assistantMessage: Message = {
        role: 'assistant',
        content: cleanedStatement,
        type: 'CONVERSATION'
      };
      setMessages([assistantMessage]);
    }
  };

  // Reset conversation
  const handleReset = () => {
    setMessages([]);
    setShowCategories(true);
    setInputValue('');
    setLanguage('english');
  };

  // Handle close/exit for mobile users
  const handleClose = () => {
    if (isWidget) {
      // If in widget mode, try to close the widget
      if (window.parent && window.parent !== window) {
        // Send message to parent window to close widget
        window.parent.postMessage({ type: 'CLOSE_WIDGET' }, '*');
      }
    } else {
      // If not in widget mode, go back in browser history or close tab
      if (window.history.length > 1) {
        window.history.back();
      } else {
        window.close();
      }
    }
  };

  // Check if we should show category buttons
  const shouldShowCategoryButtons = () => {
    // Show buttons if no messages, or if last message is an error
    if (messages.length === 0) return showCategories;
    
    const lastMessage = messages[messages.length - 1];
    const hasErrorMessage = lastMessage && lastMessage.role === 'assistant' && lastMessage.type === 'ERROR';
    
    return showCategories && hasErrorMessage;
  };

  return (
    <div className={`flex flex-col h-full max-h-screen bg-white ${isWidget ? 'widget-mode' : ''}`}>
      {/* Compact Header - Updated with brand colors and new layout */}
      <div className="text-white p-2 flex-shrink-0" style={{ background: '#012551' }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            {/* Show go back arrow instead of title when there are messages, otherwise show title */}
            {messages.length > 0 ? (
              <button
                onClick={handleReset}
                className="flex items-center px-2 py-1 rounded text-sm font-medium transition-colors hover:bg-white/10"
                style={{ color: '#ffed00' }}
                title={language === 'spanish' ? 'Volver al inicio' : 'Go back to start'}
              >
                <svg 
                  className="w-4 h-4 mr-1" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                {language === 'spanish' ? 'Volver' : 'Back'}
              </button>
            ) : (
              <h1 className="text-sm font-semibold" style={{ color: '#ffed00' }}>Somos AI Agent</h1>
            )}
          </div>
          <div className="flex-1 flex justify-center">
            {/* Replace Bills.png with your actual logo */}
            <img 
              src="/Bills.png" 
              alt="Somos Tejas Logo" 
              className="w-10 h-8 object-contain"
            />
          </div>
          <div className="flex items-center space-x-1">
            <button
              onClick={() => setLanguage(language === 'english' ? 'spanish' : 'english')}
              className="px-2 py-1 rounded text-xs font-medium transition-colors"
              style={{ backgroundColor: '#ffed00', color: '#012551' }}
            >
              {language === 'english' ? 'ES' : 'EN'}
            </button>
            <button
              onClick={handleClose}
              className="px-2 py-1 bg-red-500 hover:bg-red-400 rounded text-xs font-medium transition-colors"
              title={language === 'spanish' ? 'Cerrar' : 'Close'}
            >
              ✕
            </button>
          </div>
        </div>
      </div>

      {/* Compact Privacy Notice - Updated with brand colors */}
      <div className="p-2 flex-shrink-0" style={{ backgroundColor: '#e8f4f8', borderLeft: '4px solid #1e83fc' }}>
        <div className="flex items-center">
          <span className="mr-1 text-sm" style={{ color: '#1e83fc' }}>🔒</span>
          <p className="text-xs" style={{ color: '#012551' }}>
            {language === 'spanish' 
              ? 'Privacidad protegida — no guardamos conversaciones'
              : 'Privacy protected — we never store conversations'
            }
          </p>
          </div>
        </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-h-0">
        {/* Welcome Message or Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {shouldShowCategoryButtons() ? (
            <div className="text-center space-y-3">
              <div className="space-y-1">
                <h2 className="text-lg font-bold" style={{ color: '#012551' }}>
                  {language === 'spanish' ? 'Hola. Estás hablando con el Somos AI Agent. ¿Cómo puedo ayudarte hoy?' : 'Hi, there. You are speaking with the Somos AI Agent. How may I help you today?'}
                </h2>
                <p className="text-sm text-gray-600">
                  {language === 'spanish' 
                    ? 'Selecciona un tema o escribe tu pregunta'
                    : 'Select a topic or type your question'
                  }
                </p>
              </div>

              {/* Category Buttons - Updated with brand colors */}
              <div className="grid grid-cols-2 gap-3 max-w-sm mx-auto">
                {CATEGORY_BUTTONS.map((category) => (
                  <button
                    key={category.key}
                    onClick={() => handleCategoryClick(category)}
                    className="group relative bg-gradient-to-br from-slate-50 to-slate-100 border border-slate-200 text-slate-700 hover:text-white p-4 rounded-2xl font-medium transition-all duration-300 transform hover:scale-[1.02] hover:shadow-lg shadow-sm"
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#012551';
                      e.currentTarget.style.borderColor = '#012551';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '';
                      e.currentTarget.style.borderColor = '';
                      e.currentTarget.classList.add('bg-gradient-to-br', 'from-slate-50', 'to-slate-100', 'border-slate-200', 'text-slate-700');
                    }}
                  >
                    <div className="flex flex-col items-center space-y-2">
                      <div className="text-2xl group-hover:scale-110 transition-transform duration-300">
                        {category.icon}
                      </div>
                      <div className="text-xs leading-tight text-center">
                        {language === 'spanish' ? category.es : category.en}
                      </div>
                    </div>
                    {/* Accent line with brand yellow */}
                    <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-8 h-0.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ backgroundColor: '#ffed00' }}></div>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            // Messages
            <div className="space-y-3">
              {messages.map((message, index) => (
                <div
                  key={index}
                  className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] rounded-lg px-3 py-2 ${
                      message.role === 'user'
                        ? 'text-white'
                        : 'text-gray-800 border border-gray-200'
                    }`}
                    style={{
                      backgroundColor: message.role === 'user' ? '#012551' : '#f9fafb'
                    }}
                  >
                    <div className="text-sm whitespace-pre-wrap break-words leading-relaxed">
                      <ReactMarkdown 
                        remarkPlugins={[remarkGfm]}
                        components={{
                          // Make links open in new tab and style them
                          a: ({ node, ...props }) => (
                            <a 
                              {...props} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="underline font-medium"
                              style={{ color: '#1e83fc' }}
                            />
                          ),
                          // Style other markdown elements
                          h1: ({ node, ...props }) => <h1 {...props} className="text-lg font-bold mb-2" />,
                          h2: ({ node, ...props }) => <h2 {...props} className="text-base font-semibold mb-2" />,
                          h3: ({ node, ...props }) => <h3 {...props} className="text-sm font-semibold mb-1" />,
                          ul: ({ node, ...props }) => <ul {...props} className="list-disc list-inside mb-2" />,
                          ol: ({ node, ...props }) => <ol {...props} className="list-decimal list-inside mb-2" />,
                          li: ({ node, ...props }) => <li {...props} className="mb-1" />,
                          p: ({ node, ...props }) => <p {...props} className="mb-2 leading-relaxed" />,
                          strong: ({ node, ...props }) => <strong {...props} className="font-semibold" />,
                        }}
                      >
                        {message.content.trim()}
                      </ReactMarkdown>
                    </div>
                  </div>
                </div>
              ))}
              
              {/* Loading indicator */}
              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-gray-100 border border-gray-200 rounded-lg px-3 py-2">
                    <div className="flex items-center space-x-2">
                      <div className="flex space-x-1">
                        <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce"></div>
                        <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                        <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                      </div>
                      <span className="text-gray-600 text-xs">
                        {language === 'spanish' ? 'Escribiendo...' : 'Typing...'}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area - Updated with brand colors */}
        <div className="border-t border-gray-200 p-3 flex-shrink-0">
          <form onSubmit={handleSubmit} className="flex space-x-2">
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={
                language === 'spanish' 
                  ? 'Escribe tu pregunta...'
                  : 'Type your question...'
              }
              className="flex-1 px-3 py-2 border rounded-lg focus:outline-none text-sm transition-all duration-200"
              style={{
                borderColor: isInputFocused ? '#1e83fc' : '#d1d5db',
                backgroundColor: isInputFocused ? '#e8f4fd' : '#ffffff',
                boxShadow: isInputFocused ? '0 0 0 2px rgba(30, 131, 252, 0.2)' : 'none'
              }}
              onFocus={() => setIsInputFocused(true)}
              onBlur={() => setIsInputFocused(false)}
              disabled={isLoading}
            />
            <button
              type="submit"
              disabled={!inputValue.trim() || isLoading}
              className="px-4 py-2 text-white rounded-lg hover:opacity-90 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium text-sm"
              style={{ backgroundColor: '#012551' }}
            >
              {language === 'spanish' ? 'Enviar' : 'Send'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

